import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useReverseDivergenceBatchMutation } from "@/services/divergence";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { formatToBRL } from "@/utils/formatToMoney";
import { Undo2 } from "lucide-react";
import { useState } from "react";

import {
  BatchResultSkip,
  summarizeSkipped,
} from "../../utils/batch-result-summary";
import {
  BatchReversalSplit,
  ReversibleDivergence,
  splitBatchReversal,
} from "../../utils/reversal-effect";
import {
  REVERSAL_LABELS,
  REVERSAL_MIN_REASON_LENGTH,
  REVERSAL_SKIP_REASON_LABELS,
} from "../../utils/reversal-messages";

export const BATCH_REVERSAL_TEST_ID: string = "batch-reversal-modal";

interface BatchReversalModalProps {
  divergences: Array<ReversibleDivergence & { id: number }>;
  openCycle: string;
  onReversed: () => void;
}

export function BatchReversalModal({
  divergences,
  openCycle,
  onReversed,
}: BatchReversalModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [reverseBatch, { isLoading }] = useReverseDivergenceBatchMutation();
  const [reason, setReason] = useState<string>("");

  const split: BatchReversalSplit = splitBatchReversal(divergences, openCycle);

  const reversibleIds: number[] = (
    split.reversible as ReadonlyArray<ReversibleDivergence & { id: number }>
  ).map((item: ReversibleDivergence & { id: number }): number => item.id);

  const missingCharacters: number = Math.max(
    0,
    REVERSAL_MIN_REASON_LENGTH - reason.trim().length,
  );

  const confirm = async (): Promise<void> => {
    try {
      const result = await reverseBatch({
        divergenceIds: reversibleIds,
        reason,
      }).unwrap();

      const skippedSummary: string = summarizeSkipped(
        (result.skipped ?? []) as BatchResultSkip[],
        REVERSAL_SKIP_REASON_LABELS,
      );

      toast({
        description: skippedSummary
          ? REVERSAL_LABELS.BATCH_PARTIAL(
              result.reversed?.length ?? 0,
              skippedSummary,
            )
          : REVERSAL_LABELS.BATCH_SUCCESS(result.reversed?.length ?? 0),
        variant: skippedSummary ? "alert" : "success",
        duration: skippedSummary ? 8000 : 4000,
      });

      onReversed();
      handleModal({ isOpen: false });
    } catch (error) {
      toast({
        description: extractRtkErrorMessage(error, REVERSAL_LABELS.ERROR),
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  return (
    <div className="w-full max-w-lg space-y-4" data-testid={BATCH_REVERSAL_TEST_ID}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
          <Undo2 className="h-5 w-5 text-amber-600" />
        </div>
        <p className="font-medium text-gray-900">{REVERSAL_LABELS.BATCH_TITLE}</p>
      </div>

      <p className="rounded-md bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">
        {REVERSAL_LABELS.BATCH_TOTAL(formatToBRL(split.total_to_cancel))}
      </p>

      {split.requiresIndividual.length > 0 && (
        <p className="text-xs text-gray-600">
          {REVERSAL_LABELS.BATCH_REMOVED(split.requiresIndividual.length)}
        </p>
      )}

      <div className="space-y-1">
        <label htmlFor="batch-reversal-reason" className="text-sm text-gray-700">
          {REVERSAL_LABELS.REASON}
        </label>
        <Textarea
          id="batch-reversal-reason"
          value={reason}
          onChange={event => setReason(event.target.value)}
          className="min-h-[90px]"
        />
        {missingCharacters > 0 && (
          <p className="text-xs text-gray-500">
            {REVERSAL_LABELS.REASON_REMAINING(missingCharacters)}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => handleModal({ isOpen: false })}
          disabled={isLoading}
        >
          {REVERSAL_LABELS.CANCEL}
        </Button>
        <Button
          onClick={() => {
            void confirm();
          }}
          disabled={
            isLoading ||
            missingCharacters > 0 ||
            reversibleIds.length === 0
          }
        >
          {REVERSAL_LABELS.CONFIRM}
        </Button>
      </div>
    </div>
  );
}
